import { Box, IconButton } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type FC } from 'react';
import { PlusCircle } from '@untitledui-pro/icons/solid';
import usePersistentStore, { getLayersState, useLayersBucket } from '@gui/store/persistent/persistentStore';
import { BlockMetadata, BlockParameters, NotificationType, SerializedBlock } from '@visualizer/types';
import { BlockCatalog, BlockKeys } from '@data/Simulations/pipeline/BlockCatalog';
import PipelineParametersSection from './BlockParameters/PipelineParametersSection';
import PipelineAddMenu from './PipelineAddMenu';
import PipelineBlockList, { PIPELINE_BLOCK_SPACING } from './PipelineBlockList';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { FilterTargetResolution, resolveTargetLayer } from './filterTargetLayerResolver';
import useNotificationStore from '@gui/store/notification';
import { getGrid } from 'store/LoadingStore';
import TargetLayerSelectionDialog from './dialogs/TargetLayerSelectionDialog';

const EMPTY_BLOCKS: Array<SerializedBlock<BlockParameters>> = [];
type PendingTargetSelection = {
    filter: SerializedBlock<BlockParameters> | BlockMetadata;
    options: FilterTargetResolution[];
};

const Pipeline: FC = () => {
    const pipelineBlocks = useLayersBucket((layers) => {
        const activeLayerId = layers.activeLayerID;
        if (!activeLayerId) {
            return EMPTY_BLOCKS;
        }

        const layer = layers.layerStates[activeLayerId];
        return layer?.pipeline?.blocks ?? EMPTY_BLOCKS;
    });
    const vectorialScalarMode = usePersistentStore((state) => {
        const activeLayerId = state.layers.activeLayerID;
        if (!activeLayerId) return 0 as 0 | 1;
        return state.layers.layerStates[activeLayerId]?.VECTORIAL_SCALAR_MODE ?? 0;
    });
    const isScalarMode = vectorialScalarMode === 1;
    const blocks = useMemo(() => pipelineBlocks.filter((block) => BlockCatalog.isRegularBlock(block.type)), [pipelineBlocks]);
    const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
    const [addMenuAnchorEl, setAddMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedBlockExtraHeight, setSelectedBlockExtraHeight] = useState(0);
    const [pendingTargetSelection, setPendingTargetSelection] = useState<PendingTargetSelection | null>(null);
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);
    const placeholderHandleRef = useRef<HTMLButtonElement | null>(null);
    const pipelineController = ControllerManager.getInstance().getPipelineController();
    const availableBlocks = useMemo(() => {
        const visible = BlockCatalog.getVisibleCollection();
        const safe = visible.filter((block): block is BlockMetadata => Boolean(block?.name));
        return safe.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    }, []);
    const isAddMenuOpen = Boolean(addMenuAnchorEl);
    const hasBlocks = blocks.length > 0;
    const { showNotification } = useNotificationStore.getState();
    const scalarDisabledBlockTypes = useMemo(
        () => new Set<string>([BlockKeys.Projection, BlockKeys.Unobstructed, BlockKeys.PvPotentials, BlockKeys.ShgPotentials]),
        []
    );

    const activateTarget = (target?: FilterTargetResolution): void => {
        if (target) {
            const { layerId, layerName, frame } = target;
            const activeLayerId = getLayersState().activeLayerID;
            if (activeLayerId !== layerId) {
                usePersistentStore.getState().setCurrentLayerId(layerId);
                usePersistentStore.getState().updateLayerState(layerId, { segments: { isAggregate: false } });
                showNotification(`Switched to "${layerName}" because this filter runs on that layer.`, NotificationType.Info);
            }
            if (frame !== undefined) {
                const layer = getGrid().searchLayerByUID(layerId);
                layer.setFrame(frame);
            }
        }
    };

    function addBlock(filter: SerializedBlock<BlockParameters> | BlockMetadata): void {
        const defaults = PipelineParametersSection.getDefaultParams(filter.type);
        const initialParams = { enabled: true, ...(defaults ?? {}) } as BlockParameters;
        const newBlockID: string = pipelineController.addBlock(filter.type, initialParams);
        setSelectedBlockId(newBlockID);
    }

    const enableDisableBlock = useCallback(
        (blockId: string, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            pipelineController.toggleBlock(blockId);
            e.stopPropagation();
        },
        [pipelineController]
    );

    const deleteBlock = useCallback(
        (blockId: string, e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            pipelineController.removeBlock(blockId);
            e.stopPropagation();
        },
        [pipelineController]
    );

    const moveBlocks = useCallback(
        (id: string, proposedVisibleIndex: number) => {
            const visibleIndicesInFull = pipelineBlocks.reduce<number[]>((acc, block, index) => {
                if (BlockCatalog.isRegularBlock(block.type)) {
                    acc.push(index);
                }
                return acc;
            }, []);

            if (visibleIndicesInFull.length === 0) {
                return;
            }

            const clampedVisibleIndex = Math.max(0, Math.min(proposedVisibleIndex, visibleIndicesInFull.length - 1));
            const targetAbsoluteIndex = visibleIndicesInFull[clampedVisibleIndex] ?? clampedVisibleIndex;

            pipelineController.moveBlock(id, targetAbsoluteIndex);
        },
        [pipelineBlocks, pipelineController]
    );

    const handleSelectBlock = useCallback((blockId: string) => {
        setSelectedBlockId((prev) => (prev === blockId ? null : blockId));
    }, []);
    const handleDragStart = useCallback(() => {
        setSelectedBlockId(null);
    }, []);

    const handleOpenAddMenuFromButton = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        setAddMenuAnchorEl(event.currentTarget);
    };

    const handleOpenAddMenuFromPlaceholder = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setAddMenuAnchorEl(placeholderHandleRef.current ?? event.currentTarget);
    };

    const handlePlaceholderKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            setAddMenuAnchorEl(placeholderHandleRef.current ?? event.currentTarget);
        }
    };

    const handleCloseAddMenu = () => {
        setAddMenuAnchorEl(null);
    };

    const handleSelectAvailableBlock = (blockFilter: SerializedBlock<BlockParameters> | BlockMetadata) => {
        const resolved = resolveTargetLayer(BlockCatalog.getBlockMetadata(blockFilter.type));
        if (resolved.error) {
            showNotification(resolved.error.message, NotificationType.Error);
            return;
        }
        if (resolved.selection && resolved.selection.length > 0) {
            setPendingTargetSelection({
                filter: blockFilter,
                options: resolved.selection,
            });
            return;
        }

        if (resolved.target) activateTarget(resolved.target);
        addBlock(blockFilter);
    };

    useEffect(() => {
        if (!hasBlocks) {
            setSelectedBlockExtraHeight(0);
        }
    }, [hasBlocks]);

    useEffect(() => {
        if (!isScalarMode) return;
        const blocked = blocks.filter((block) => scalarDisabledBlockTypes.has(block.type) && block.params.enabled);
        if (blocked.length === 0) return;

        blocked.forEach((block) => {
            pipelineController.toggleBlock(block.id, false);
        });
        const disabledNames = blocked.map((block) => BlockCatalog.getBlockMetadata(block.type)?.name ?? block.type).join(', ');
        const verb = blocked.length === 1 ? 'is' : 'are';
        const noun = blocked.length === 1 ? 'filter' : 'filters';
        const disabledVerb = blocked.length === 1 ? 'it has' : 'they have';
        showNotification(
            `${disabledNames} ${noun} ${verb} not available in scalar mode, ${disabledVerb} been disabled in filters pipeline`,
            NotificationType.Info
        );
    }, [isScalarMode, blocks, pipelineController, scalarDisabledBlockTypes, showNotification]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    position: 'relative',
                }}
                ref={scrollContainerRef}
            >
                <Box
                    className="pipeline-lane"
                    style={
                        {
                            minHeight: PIPELINE_BLOCK_SPACING * Math.max(0, blocks.length - 1) + 90 + selectedBlockExtraHeight,
                        } as CSSProperties
                    }
                >
                    {hasBlocks && (
                        <Box className="pipeline-vertical-rail">
                            <Box className="pipeline-vertical-line" />
                            <IconButton
                                className="pipeline-add-handle-button"
                                onClick={handleOpenAddMenuFromButton}
                                aria-label="Add filter"
                            >
                                <PlusCircle className="icon-size-big" style={{ color: 'inherit' }} />
                            </IconButton>
                        </Box>
                    )}
                    {hasBlocks ? (
                        <PipelineBlockList
                            blocks={blocks}
                            selectedBlockId={selectedBlockId}
                            onSelect={handleSelectBlock}
                            onToggle={enableDisableBlock}
                            onDelete={deleteBlock}
                            onMove={moveBlocks}
                            onDragStart={handleDragStart}
                            scrollContainerRef={scrollContainerRef}
                            onSelectedBlockExtraHeightChange={setSelectedBlockExtraHeight}
                        />
                    ) : (
                        <Box
                            className="pipeline-block pipeline-block--placeholder"
                            role="button"
                            tabIndex={0}
                            onClick={handleOpenAddMenuFromPlaceholder}
                            onKeyDown={handlePlaceholderKeyDown}
                            aria-label="Click to add a filter"
                        >
                            <IconButton
                                ref={placeholderHandleRef}
                                className="pipeline-add-handle-button pipeline-placeholder-add-handle-button"
                                onClick={handleOpenAddMenuFromButton}
                                aria-label="Add filter"
                            >
                                <PlusCircle className="icon-size-big" style={{ color: 'inherit' }} />
                            </IconButton>
                            <Box className="pipeline-block-header">
                                <span className="pipeline-block-placeholder-text">Click to add a filter</span>
                            </Box>
                        </Box>
                    )}
                </Box>
                <PipelineAddMenu
                    anchorEl={addMenuAnchorEl}
                    open={isAddMenuOpen}
                    isScalarMode={isScalarMode}
                    availableBlocks={availableBlocks}
                    onSelect={handleSelectAvailableBlock}
                    onClose={handleCloseAddMenu}
                />

                <TargetLayerSelectionDialog
                    open={Boolean(pendingTargetSelection)}
                    options={pendingTargetSelection?.options ?? []}
                    onCancel={() => setPendingTargetSelection(null)}
                    onSelect={(option) => {
                        if (!pendingTargetSelection) return;
                        activateTarget(option);
                        addBlock(pendingTargetSelection.filter);
                        setPendingTargetSelection(null);
                    }}
                />
            </Box>
        </Box>
    );
};

export default Pipeline;
